import React from "react";
import { Link } from "react-router-dom";
import {
  QrCode,
  Sparkles,
  TrendingUp,
  ArrowRight,
  Shield,
  Star,
  Home,
  Check,
  ChefHat,
  Car,
  Heart,
  Camera,
  Music,
} from "lucide-react";
// Shared marketing-page tokens + tiny atoms.
import {
  IVORY, NAVY, EMERALD, GOLD, OCEAN, CORAL,
  Eyebrow, TrustChip,
} from "@/components/marketing/primitives";

const HERO_IMG =
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1600&q=80";
const HERO_IMG_2 =
  "https://images.unsplash.com/photo-1551776235-dde6d482980b?auto=format&fit=crop&w=900&q=80";
const HERO_IMG_3 =
  "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=900&q=80";

/* --- Page ------------------------------------------------------------- */
export default function BecomeHost() {
  return (
    <div data-testid="page-become-host" style={{ background: IVORY }}>
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Copy */}
            <div className="lg:col-span-7">
              <Eyebrow>For Property Owners</Eyebrow>
              <h1
                className="font-serif mt-4 leading-[1.02] tracking-tight"
                style={{
                  color: NAVY,
                  fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
                }}
                data-testid="host-hero-headline"
              >
                Turn Every Stay
                <br className="hidden sm:block" /> Into a{" "}
                <span style={{ color: EMERALD }}>5-Star</span> Experience.
              </h1>
              <p
                className="mt-5 sm:mt-6 text-base sm:text-lg max-w-xl leading-relaxed"
                style={{ color: "rgba(7,26,45,0.72)" }}
                data-testid="host-hero-subtext"
              >
                Give your guests access to premium concierge experiences without
                hiring staff.
              </p>

              {/* CTAs */}
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to="/signup?role=host"
                  className="btn-coral inline-flex items-center gap-2 text-base"
                  data-testid="host-hero-cta-list"
                >
                  List your property <ArrowRight size={18} />
                </Link>
                <Link
                  to="/how-it-works#hosts"
                  className="btn-outline-emerald text-sm sm:text-base"
                  data-testid="host-hero-cta-learn"
                >
                  See how hosting works
                </Link>
              </div>

              {/* Trust chips */}
              <div className="mt-8 flex flex-wrap gap-2.5">
                <TrustChip icon={QrCode} label="No app for guests" accent={OCEAN} />
                <TrustChip icon={TrendingUp} label="Passive income" accent={EMERALD} />
                <TrustChip icon={Star} label="5-star reviews" accent={GOLD} />
              </div>
            </div>

            {/* Image collage */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div
                  className="rounded-[28px] overflow-hidden shadow-luxury-lg border"
                  style={{ borderColor: "rgba(7,26,45,0.08)" }}
                >
                  <img
                    src={HERO_IMG}
                    alt="Luxury vacation property interior"
                    className="w-full h-[360px] sm:h-[460px] object-cover"
                    loading="eager"
                  />
                </div>

                {/* Floating QR concierge preview */}
                <div
                  className="hidden sm:flex absolute -left-6 bottom-8 items-center gap-3 bg-white rounded-2xl px-4 py-3 shadow-luxury-lg border"
                  style={{ borderColor: "rgba(7,26,45,0.08)" }}
                >
                  <div
                    className="w-10 h-10 rounded-xl inline-flex items-center justify-center"
                    style={{ background: `${OCEAN}15`, color: OCEAN }}
                  >
                    <QrCode size={20} />
                  </div>
                  <div className="text-xs leading-tight" style={{ color: NAVY }}>
                    <div className="font-semibold">Guest concierge ready</div>
                    <div style={{ color: "rgba(7,26,45,0.55)" }}>
                      One scan to book experiences
                    </div>
                  </div>
                </div>

                <div
                  className="hidden lg:block absolute -right-6 -top-6 w-36 h-36 rounded-2xl overflow-hidden shadow-luxury border-2"
                  style={{ borderColor: IVORY }}
                >
                  <img
                    src={HERO_IMG_2}
                    alt="Luxury bedroom"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div
                  className="hidden lg:block absolute -right-2 -bottom-8 w-32 h-32 rounded-2xl overflow-hidden shadow-luxury border-2"
                  style={{ borderColor: IVORY }}
                >
                  <img
                    src={HERO_IMG_3}
                    alt="Property exterior with pool"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== BENEFIT CARDS ============== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24">
        <div className="max-w-2xl">
          <Eyebrow>Hospitality, Elevated</Eyebrow>
          <h2
            className="font-serif mt-3 leading-tight"
            style={{ color: NAVY, fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
          >
            A luxury concierge — for every property, every guest.
          </h2>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5 sm:gap-6">
          {/* CARD 1 — QR Concierge */}
          <article
            className="group relative rounded-3xl bg-white border p-7 sm:p-8 shadow-luxury transition-all duration-300 hover:-translate-y-1 hover:shadow-luxury-lg flex flex-col"
            style={{ borderColor: "rgba(7,26,45,0.08)" }}
            data-testid="host-benefit-qr"
          >
            <div
              className="w-12 h-12 rounded-2xl inline-flex items-center justify-center"
              style={{ background: `${OCEAN}15`, color: OCEAN }}
            >
              <QrCode size={22} />
            </div>
            <h3 className="font-serif text-2xl mt-5" style={{ color: NAVY }}>
              QR Concierge
            </h3>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ color: "rgba(7,26,45,0.72)" }}
            >
              Create a personalized concierge page your guests can access
              instantly.
            </p>

            {/* Mini mock QR */}
            <div className="mt-6 flex items-center gap-3">
              <div
                className="w-16 h-16 rounded-xl border-2 flex items-center justify-center"
                style={{ borderColor: `${OCEAN}55`, background: `${OCEAN}08` }}
              >
                <QrCode size={28} style={{ color: OCEAN }} />
              </div>
              <div className="text-xs" style={{ color: "rgba(7,26,45,0.65)" }}>
                <div className="font-semibold" style={{ color: NAVY }}>
                  No download required
                </div>
                Works on every guest&apos;s phone, instantly.
              </div>
            </div>
          </article>

          {/* CARD 2 — Enhance Stay */}
          <article
            className="group relative rounded-3xl border p-7 sm:p-8 shadow-luxury transition-all duration-300 hover:-translate-y-1 hover:shadow-luxury-lg flex flex-col"
            style={{
              background: `linear-gradient(155deg, ${EMERALD} 0%, #0a6b53 100%)`,
              borderColor: EMERALD,
              color: IVORY,
            }}
            data-testid="host-benefit-enhance"
          >
            <div
              className="w-12 h-12 rounded-2xl inline-flex items-center justify-center"
              style={{ background: "rgba(255,248,238,0.14)", color: IVORY }}
            >
              <Sparkles size={22} />
            </div>
            <h3 className="font-serif text-2xl mt-5" style={{ color: IVORY }}>
              Enhance Every Stay
            </h3>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ color: "rgba(255,248,238,0.82)" }}
            >
              Offer chefs, drivers, tours, wellness, events, and premium
              experiences.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-2">
              {[
                { i: ChefHat, l: "Chefs" },
                { i: Car, l: "Drivers" },
                { i: Camera, l: "Tours" },
                { i: Heart, l: "Wellness" },
                { i: Music, l: "Events" },
                { i: Sparkles, l: "Premium" },
              ].map(({ i: Icon, l }) => (
                <div
                  key={l}
                  className="rounded-xl px-2 py-2.5 bg-white/10 border border-white/15 flex flex-col items-center gap-1"
                >
                  <Icon size={16} />
                  <span className="text-[10px] uppercase tracking-wider opacity-85">
                    {l}
                  </span>
                </div>
              ))}
            </div>
          </article>

          {/* CARD 3 — Passive Income */}
          <article
            className="group relative rounded-3xl bg-white border p-7 sm:p-8 shadow-luxury transition-all duration-300 hover:-translate-y-1 hover:shadow-luxury-lg flex flex-col"
            style={{ borderColor: "rgba(7,26,45,0.08)" }}
            data-testid="host-benefit-income"
          >
            <div
              className="w-12 h-12 rounded-2xl inline-flex items-center justify-center"
              style={{ background: `${GOLD}22`, color: "#8a6a2e" }}
            >
              <TrendingUp size={22} />
            </div>
            <h3 className="font-serif text-2xl mt-5" style={{ color: NAVY }}>
              Earn Passive Income
            </h3>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ color: "rgba(7,26,45,0.72)" }}
            >
              Earn a referral commission when guests book experiences through
              your host page.
            </p>

            <div
              className="mt-6 rounded-2xl p-4 border flex items-baseline justify-between"
              style={{
                background: `${GOLD}10`,
                borderColor: `${GOLD}55`,
              }}
            >
              <div
                className="text-[11px] uppercase tracking-wider font-semibold"
                style={{ color: "#8a6a2e" }}
              >
                Host commission
              </div>
              <div className="font-serif text-2xl" style={{ color: NAVY }}>
                5%
              </div>
            </div>
            <div className="mt-3 text-xs" style={{ color: "rgba(7,26,45,0.55)" }}>
              Per referred booking. No staff. No overhead.
            </div>
          </article>
        </div>
      </section>

      {/* ============== PAYMENT LOGIC ============== */}
      <section
        className="py-16 sm:py-24"
        style={{ background: "rgba(7,26,45,0.03)" }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <Eyebrow>Transparent Payments</Eyebrow>
            <h2
              className="font-serif mt-3 leading-tight"
              style={{ color: NAVY, fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
            >
              See exactly how every booking is split.
            </h2>
            <p
              className="mt-4 text-base leading-relaxed"
              style={{ color: "rgba(7,26,45,0.65)" }}
            >
              Two simple splits depending on how the guest discovers the
              experience. Example shown for a <b>$500</b> customer payment.
            </p>
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-5 sm:gap-6">
            {/* Host Referral Booking */}
            <div
              className="rounded-3xl bg-white border p-7 sm:p-8 shadow-luxury"
              style={{ borderColor: "rgba(7,26,45,0.08)" }}
              data-testid="host-split-referral"
            >
              <div className="flex items-center justify-between">
                <Eyebrow color={EMERALD}>Host Referral Booking</Eyebrow>
                <span
                  className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full font-semibold"
                  style={{ background: `${GOLD}22`, color: "#8a6a2e" }}
                >
                  Recommended
                </span>
              </div>
              <h3
                className="font-serif text-2xl mt-3"
                style={{ color: NAVY }}
              >
                Guest books through your concierge
              </h3>
              <div
                className="mt-5 text-sm"
                style={{ color: "rgba(7,26,45,0.65)" }}
              >
                Customer pays{" "}
                <span className="font-serif text-xl" style={{ color: NAVY }}>
                  $500
                </span>
              </div>

              {/* Bar */}
              <div className="mt-4 h-2 w-full rounded-full overflow-hidden flex">
                <div style={{ width: "80%", background: EMERALD }} />
                <div style={{ width: "15%", background: NAVY }} />
                <div style={{ width: "5%", background: GOLD }} />
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <div
                  className="rounded-2xl p-4 border"
                  style={{
                    borderColor: `${EMERALD}33`,
                    background: `${EMERALD}08`,
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: EMERALD }}>
                    Provider · 80%
                  </div>
                  <div className="font-serif text-xl mt-1" style={{ color: NAVY }}>
                    $400
                  </div>
                </div>
                <div
                  className="rounded-2xl p-4 border"
                  style={{
                    borderColor: "rgba(7,26,45,0.18)",
                    background: "rgba(7,26,45,0.04)",
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: NAVY }}>
                    TREL · 15%
                  </div>
                  <div className="font-serif text-xl mt-1" style={{ color: NAVY }}>
                    $75
                  </div>
                </div>
                <div
                  className="rounded-2xl p-4 border"
                  style={{
                    borderColor: `${GOLD}66`,
                    background: `${GOLD}14`,
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#8a6a2e" }}>
                    Host · 5%
                  </div>
                  <div className="font-serif text-xl mt-1" style={{ color: NAVY }}>
                    $25
                  </div>
                </div>
              </div>
            </div>

            {/* Direct Booking */}
            <div
              className="rounded-3xl bg-white border p-7 sm:p-8 shadow-luxury"
              style={{ borderColor: "rgba(7,26,45,0.08)" }}
              data-testid="host-split-direct"
            >
              <Eyebrow color={OCEAN}>Direct Booking</Eyebrow>
              <h3
                className="font-serif text-2xl mt-3"
                style={{ color: NAVY }}
              >
                Guest books directly on TREL
              </h3>
              <div
                className="mt-5 text-sm"
                style={{ color: "rgba(7,26,45,0.65)" }}
              >
                Customer pays{" "}
                <span className="font-serif text-xl" style={{ color: NAVY }}>
                  $500
                </span>
              </div>

              <div className="mt-4 h-2 w-full rounded-full overflow-hidden flex">
                <div style={{ width: "80%", background: EMERALD }} />
                <div style={{ width: "20%", background: NAVY }} />
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <div
                  className="rounded-2xl p-4 border"
                  style={{
                    borderColor: `${EMERALD}33`,
                    background: `${EMERALD}08`,
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: EMERALD }}>
                    Concierge · 80%
                  </div>
                  <div className="font-serif text-xl mt-1" style={{ color: NAVY }}>
                    $400
                  </div>
                </div>
                <div
                  className="rounded-2xl p-4 border"
                  style={{
                    borderColor: "rgba(7,26,45,0.18)",
                    background: "rgba(7,26,45,0.04)",
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: NAVY }}>
                    TREL · 20%
                  </div>
                  <div className="font-serif text-xl mt-1" style={{ color: NAVY }}>
                    $100
                  </div>
                </div>
                <div
                  className="rounded-2xl p-4 border opacity-60"
                  style={{
                    borderColor: "rgba(7,26,45,0.12)",
                    background: "rgba(7,26,45,0.02)",
                  }}
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "rgba(7,26,45,0.55)" }}>
                    Host · 0%
                  </div>
                  <div className="font-serif text-xl mt-1" style={{ color: "rgba(7,26,45,0.45)" }}>
                    $0
                  </div>
                </div>
              </div>

              <div className="mt-5 text-xs" style={{ color: "rgba(7,26,45,0.55)" }}>
                Host commissions apply only when guests book through your
                personalized concierge page.
              </div>
            </div>
          </div>

          {/* Tips note */}
          <div
            className="mt-6 rounded-2xl px-5 py-4 text-xs sm:text-sm border"
            style={{
              background: "rgba(0,174,239,0.06)",
              borderColor: "rgba(0,174,239,0.22)",
              color: "rgba(7,26,45,0.75)",
            }}
          >
            <b style={{ color: NAVY }}>Tips:</b> 100% of any customer tip goes
            directly to the Concierge. TREL takes no cut on tips.
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS (HOST FLOW) ============== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="max-w-2xl">
          <Eyebrow>From your QR to their moment</Eyebrow>
          <h2
            className="font-serif mt-3 leading-tight"
            style={{ color: NAVY, fontSize: "clamp(1.9rem, 3.4vw, 2.75rem)" }}
          >
            Four steps to passive concierge income.
          </h2>
        </div>

        <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="host-flow-list">
          {[
            { n: "01", t: "List your property", d: "Add your rental or stay to your host dashboard.", i: Home },
            { n: "02", t: "Get your QR concierge", d: "We generate a personalized guest page for each property.", i: QrCode },
            { n: "03", t: "Place QR in your space", d: "Guests scan, browse experiences, and book in seconds.", i: Sparkles },
            { n: "04", t: "Earn 5% on referrals", d: "Track bookings and payouts inside your host dashboard.", i: TrendingUp },
          ].map(({ n, t, d, i: Icon }) => (
            <li
              key={n}
              className="rounded-3xl bg-white border p-6 sm:p-7 shadow-luxury transition-all duration-300 hover:-translate-y-0.5 hover:shadow-luxury-lg"
              style={{ borderColor: "rgba(7,26,45,0.08)" }}
              data-testid={`host-flow-step-${n}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-serif text-3xl" style={{ color: GOLD }}>
                  {n}
                </div>
                <div
                  className="w-10 h-10 rounded-xl inline-flex items-center justify-center"
                  style={{ background: `${EMERALD}10`, color: EMERALD }}
                >
                  <Icon size={18} />
                </div>
              </div>
              <h3 className="font-serif text-lg mt-4 leading-snug" style={{ color: NAVY }}>
                {t}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(7,26,45,0.65)" }}>
                {d}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* ============== TRUST DISCLAIMER ============== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12">
        <div
          className="rounded-2xl px-5 py-4 text-xs sm:text-sm border flex items-start gap-3"
          style={{
            background: "rgba(0,174,239,0.06)",
            borderColor: "rgba(0,174,239,0.25)",
            color: "rgba(7,26,45,0.75)",
          }}
        >
          <Shield size={16} style={{ color: OCEAN, marginTop: 2 }} />
          <div>
            Every experience your guests can book is created by an independent,
            trusted TREL Concierge — identity verified and TREL Certified before they can deliver experiences.
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
        <div
          className="rounded-[28px] p-8 sm:p-12 lg:p-16 text-center relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${EMERALD} 0%, #0a6b53 60%, ${NAVY} 100%)`,
            color: IVORY,
          }}
        >
          <div className="relative z-10 max-w-3xl mx-auto">
            <Eyebrow color={GOLD}>Ready when you are</Eyebrow>
            <h2
              className="font-serif mt-3 leading-tight"
              style={{ fontSize: "clamp(1.9rem, 4vw, 3rem)", color: IVORY }}
            >
              Give every guest a 5-star arrival.
            </h2>
            <p
              className="mt-4 text-base sm:text-lg leading-relaxed"
              style={{ color: "rgba(255,248,238,0.82)" }}
            >
              List your property today and start earning concierge commissions
              on every guest experience.
            </p>
            <div className="mt-7 flex flex-wrap justify-center items-center gap-3">
              <Link
                to="/signup?role=host"
                className="btn-coral inline-flex items-center gap-2 text-base"
                data-testid="host-final-cta"
              >
                List your property <ArrowRight size={18} />
              </Link>
              <Link
                to="/how-it-works#hosts"
                className="text-sm font-semibold underline-offset-4 hover:underline"
                style={{ color: "rgba(255,248,238,0.85)" }}
              >
                Learn how hosting works
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3 text-xs" style={{ color: "rgba(255,248,238,0.7)" }}>
              <div className="inline-flex items-center gap-1.5"><QrCode size={13}/> Instant guest concierge</div>
              <div className="inline-flex items-center gap-1.5"><TrendingUp size={13}/> 5% on every referral</div>
              <div className="inline-flex items-center gap-1.5"><Star size={13}/> Better reviews, more repeats</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
